/**
 * Phase 7++++ — DishListEditor
 *
 * Compact row-list of `Dish` objects:
 *   { name, material_id, material_name }
 *
 * Each row renders as
 *   [ Dish name input ───────── ] [ Material ▼ ] [ × ]
 *
 * Designed for buffet contexts (Hot/Cold/Pastry stations, Live Station
 * dishes, Buffet Line items) where every dish can optionally be tagged
 * with a service-ware material (Paella Pan, Bon Chef Chafer, etc.).
 *
 * Materials come from `lib_buffet_materials` and are passed in via the
 * `materialsLib` prop (an array of `{id, name, notes?}`). The dropdown
 * also exposes a "(none)" sentinel so a dish can be material-less.
 *
 * Storage shape matches the backend `Dish` Pydantic model exactly:
 *   {
 *     name:          str,
 *     material_id:   Optional[str],    // lib_buffet_materials.id
 *     material_name: Optional[str],    // snapshot of material.name
 *   }
 * — keeping `material_name` denormalised so deleting a material from
 * the library doesn't erase already-tagged dishes (per Turn 2 snapshot
 * pattern used elsewhere in the app).
 */
import { useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { Plus, X, ArrowDownAZ, ClipboardCopy } from "lucide-react";
import CopyDishesFromStationDialog from "@/components/CopyDishesFromStationDialog";

const NONE = "__none__";

export default function DishListEditor({
  value,
  onChange,
  materialsLib,
  tidPrefix = "dish-list",
  placeholder = "Dish name",
  addLabel = "Add dish",
  compact = false,
}) {
  const dishes = Array.isArray(value) ? value : [];

  // Ref-array keyed by row index so the Enter-handler can punt focus to
  // the freshly-appended row. Recreated on every render but React will
  // re-attach the refs through the `ref` callback below.
  const inputRefs = useRef([]);
  // `pendingFocusIdx` lets the focus jump survive the React render that
  // happens between calling `add()` and the new row's <Input> actually
  // mounting. Cleared once the focus has landed.
  const [pendingFocusIdx, setPendingFocusIdx] = useState(null);
  useEffect(() => {
    if (pendingFocusIdx == null) return;
    const el = inputRefs.current[pendingFocusIdx];
    if (el) {
      el.focus();
      setPendingFocusIdx(null);
    }
  }, [pendingFocusIdx, dishes.length]);

  const update = (idx, patch) =>
    onChange(dishes.map((d, i) => (i === idx ? { ...d, ...patch } : d)));

  const remove = (idx) => onChange(dishes.filter((_, i) => i !== idx));

  // Phase 13.3 — Sort A→Z. Case-insensitive locale-aware compare; the
  // dish object (with its material refs + stable _key) travels intact
  // so the sort doesn't lose any per-row state. Empty names sort last.
  const sortAZ = () => {
    const cmp = (a, b) => {
      const an = (a.name || "").trim();
      const bn = (b.name || "").trim();
      if (!an && !bn) return 0;
      if (!an) return 1;
      if (!bn) return -1;
      return an.localeCompare(bn, undefined, { sensitivity: "base" });
    };
    onChange([...dishes].sort(cmp));
  };

  const add = () =>
    onChange([
      ...dishes,
      // Phase 8 code-quality — stable client-side key per dish row so
      // React's reconciler doesn't shuffle inputs / lose focus when the
      // user reorders or deletes. `_key` is stripped server-side by
      // Pydantic's strict `extra="ignore"` config on the Dish model.
      {
        _key: (typeof crypto !== "undefined" && crypto.randomUUID)
          ? crypto.randomUUID()
          : `d_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
        name: "", material_id: null, material_name: "",
      },
    ]);

  // Phase 7+/T2-blocker — Enter inside a dish-name input:
  //   * value present  → append a new dish row + focus it (mirrors the
  //                      "Enter for newline" mental model from the old
  //                      textarea UX; this is the keystroke-efficient
  //                      replacement for the multilineList textarea on
  //                      Live Stations / Standard Stations / station
  //                      dishes inside StationsEditor).
  //   * value empty    → no-op (don't spam empty rows, don't submit the
  //                      surrounding dialog form).
  // Shift+Enter behaves identically — a single-line <input> can't hold
  // a literal newline so there's no distinction worth making.
  const onDishKeyDown = (e, idx) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    const cur = (dishes[idx]?.name || "").trim();
    if (!cur) return;
    // Append-and-focus only when we're on the last row OR the next row
    // is already an empty placeholder. Mid-list Enter just focuses the
    // already-existing next row so we don't shuffle the list.
    const nextIdx = idx + 1;
    const nextExists = nextIdx < dishes.length;
    if (nextExists) {
      setPendingFocusIdx(nextIdx);
      return;
    }
    add();
    setPendingFocusIdx(nextIdx);
  };

  const pickMaterial = (idx, mid) => {
    if (mid === NONE) {
      update(idx, { material_id: null, material_name: "" });
      return;
    }
    const m = (materialsLib || []).find((x) => x.id === mid);
    update(idx, {
      material_id: m ? m.id : null,
      material_name: m ? m.name : "",
    });
  };

  const rowH = compact ? "h-7" : "h-8";
  const txt = compact ? "text-xs" : "text-sm";

  // Phase 16 (F12) — Copy Dishes from Existing Station dialog state.
  const [copyOpen, setCopyOpen] = useState(false);
  const handlePicked = (picked, { mode }) => {
    // Picked dishes carry no `_key` — add one so React + the existing
    // add()/remove() pattern can index them stably.
    const withKeys = picked.map((d) => ({
      ...d,
      _key: (typeof crypto !== "undefined" && crypto.randomUUID)
        ? crypto.randomUUID()
        : `d_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
    }));
    if (mode === "replace") {
      onChange(withKeys);
    } else {
      onChange([...dishes, ...withKeys]);
    }
  };

  return (
    <div className="space-y-1" data-testid={tidPrefix}>
      {/* Phase 16 (F12) — Copy dishes button. Always visible at the top
          of every station-shaped dish list editor (Standard / Live /
          Crew Live / Theme Buffet station card / Pizza station). */}
      <div className="mb-1 flex items-center justify-between">
        <button
          type="button"
          onClick={() => setCopyOpen(true)}
          title="Copy dishes from another saved station"
          className="inline-flex items-center gap-1 rounded border border-amber-300 bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-800 hover:bg-amber-100"
          data-testid={`${tidPrefix}-copy-from-station`}
        >
          <ClipboardCopy className="h-3 w-3" />
          Copy dishes from existing station…
        </button>
      </div>
      <CopyDishesFromStationDialog
        open={copyOpen}
        onOpenChange={setCopyOpen}
        currentDishes={dishes}
        onPick={handlePicked}
      />
      {/* Phase 13.3 — Sort A→Z header bar. Shown only when there are
          ≥2 dishes (sorting 0/1 items is meaningless). The button is
          subtle so it doesn't compete with the per-row Add control at
          the bottom; positioned at the top per the user brief. */}
      {dishes.length >= 2 && (
        <div className="mb-1 flex justify-end">
          <button
            type="button"
            onClick={sortAZ}
            title="Sort dishes alphabetically (A→Z)"
            aria-label="Sort dishes alphabetically"
            className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] text-muted-foreground hover:bg-slate-100 hover:text-slate-700"
            data-testid={`${tidPrefix}-sort-az`}
          >
            <ArrowDownAZ className="h-3 w-3" />
            Sort A→Z
          </button>
        </div>
      )}
      {dishes.length === 0 && (
        <p className="text-[10px] italic text-muted-foreground">
          No dishes yet — click “+ {addLabel}” or just press Enter on the first row.
        </p>
      )}
      {dishes.map((d, idx) => {
        // The current material may have been deleted from the library
        // after this dish was tagged. We still surface the saved
        // material_name as a sticky orphan option so the user sees what
        // it was, can keep it, or pick a new one.
        const matId = d.material_id || "";
        const matInLib = (materialsLib || []).some((m) => m.id === matId);
        const orphanName = d.material_name || "";
        const pickerValue = matId && matInLib ? matId : matId ? matId : NONE;
        return (
          <div
            // Stable composite: client-stamped `_key`, then dish-bound
            // `material_id`, else legacy index. Dish rows pre-dating
            // this fix in saved meal plans fall back to `idx` (same
            // behaviour as before — no regression).
            key={d._key || d.material_id || `legacy-dish-${idx}`}
            className="flex items-center gap-1.5"
            data-testid={`${tidPrefix}-row-${idx}`}
          >
            {/* Phase 13.2 — display-only 1-based index. Never saved to
                the data shape; recomputed on every render so add /
                remove / sort stay in sync automatically. */}
            <span
              className={`shrink-0 w-6 text-right tabular-nums text-[10px] font-medium text-slate-400`}
              data-testid={`${tidPrefix}-num-${idx}`}
            >
              {idx + 1}.
            </span>
            <Input
              ref={(el) => { inputRefs.current[idx] = el; }}
              value={d.name || ""}
              onChange={(e) => update(idx, { name: e.target.value })}
              onKeyDown={(e) => onDishKeyDown(e, idx)}
              placeholder={placeholder}
              className={`${rowH} flex-1 ${txt}`}
              data-testid={`${tidPrefix}-name-${idx}`}
            />
            <Select
              value={pickerValue}
              onValueChange={(v) => pickMaterial(idx, v)}
            >
              <SelectTrigger
                className={`${rowH} w-44 shrink-0 ${txt}`}
                data-testid={`${tidPrefix}-material-${idx}`}
              >
                <SelectValue placeholder="Material…" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE}>(no material)</SelectItem>
                {(materialsLib || []).map((m) => (
                  <SelectItem key={m.id} value={m.id}>
                    {m.name}
                  </SelectItem>
                ))}
                {matId && !matInLib && (
                  <SelectItem value={matId}>
                    {orphanName || "(deleted material)"} · stale
                  </SelectItem>
                )}
              </SelectContent>
            </Select>
            <button
              type="button"
              onClick={() => remove(idx)}
              className="shrink-0 rounded p-1 text-red-600 hover:bg-red-50"
              aria-label="Remove dish"
              data-testid={`${tidPrefix}-remove-${idx}`}
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        );
      })}
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={add}
        className={`${compact ? "h-6 px-2 text-[10px]" : "h-7 px-2 text-xs"} mt-1`}
        data-testid={`${tidPrefix}-add`}
      >
        <Plus className={compact ? "h-3 w-3" : "h-3.5 w-3.5"} />
        {addLabel}
      </Button>
    </div>
  );
}
